import {useMutation, useSuspenseQuery} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {FormProvider, SubmitHandler, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import {useBeforeUnload, useBlocker, useLocation, useNavigate, useSearchParams} from "react-router";
import * as Yup from "yup";

import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {NewCheckVaultItemsContext} from "@/components/context/NewCheckVaultItemsContextInterface.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {TabContentContainer} from "@/components/layout/TabContentContainer.tsx";
import {UnsavedIndicator} from "@/components/unsavedIndicator/UnsavedIndicator.tsx";
import {accountCanPurchase} from "@/lib/applogic/applogic.ts";
import {ServiceField} from "@/lib/constants/service.ts";
import {
  CheckFormResult,
  GQL_CHECK_FORM,
  GQL_CHECKS_QUERY2,
  GQL_UPDATE_CHECK,
  UpdateCheckResponse,
} from "@/lib/queries/checks.ts";
import {AccountSubscriptionType} from "@/lib/types/accounts.ts";
import {Plan} from "@/lib/types/billing.ts";
import {MonitoringServiceType, ThirdPartyMonitoringType} from "@/lib/types/checks.ts";
import {ResponseStatus, ServerErrors} from "@/lib/types/common.ts";
import {LimitCheckServiceLimit as LimitCheckServiceLimitV1} from "@/pages/billing/v1/instantCheckout/LimitCheckServiceLimit.tsx";
import {LimitCheckServiceLimit as LimitCheckServiceLimitV2} from "@/pages/billing/v2/InstantCheckout/LimitCheckServiceLimit.tsx";
import {LimitCheckerComponent} from "@/pages/billing/v2/InstantCheckout/LimitCheckWithInstantCheckout.tsx";
import {InstantCheckout} from "@/pages/checks/components/form/InstantCheckout/InstantCheckout.tsx";
import {generateFormDefaults, getDefaultMonitoringGroups} from "@/pages/checks/components/form/utils.tsx";
import {advancedFields, baseSections, QUERY} from "@/pages/checks/constants.ts";
import {
  CreateCheckFormProps,
  CreateCheckFormSections,
  CreateCheckFormValues,
} from "@/pages/checks/update/CreateCheckForm.types.ts";
import {CheckAggregationForm} from "@/pages/checks/update/sections/CheckAggregationForm.tsx";
import {CheckDetailsForm} from "@/pages/checks/update/sections/CheckDetailsForm.tsx";
import {CheckEscalationForm} from "@/pages/checks/update/sections/CheckEscalationForm.tsx";
import {CheckMaintenanceFormV2} from "@/pages/checks/update/sections/CheckMaintenanceFormV2.tsx";
import {CheckSensitivityAndTargetingForm} from "@/pages/checks/update/sections/CheckSensitivityAndTargetingForm.tsx";
import {CheckSettingsForm} from "@/pages/checks/update/sections/CheckSettingsForm.tsx";
import {CheckSnippetModal} from "@/pages/checks/update/sections/CheckSnippetModal.tsx";
import {CheckStepsForm} from "@/pages/checks/update/sections/CheckStepsForm.tsx";
import {CheckTestRunnerForm} from "@/pages/checks/update/sections/CheckTestRunnerForm.tsx";
import {CheckUnsavedModal} from "@/pages/checks/update/sections/CheckUnsavedModal.tsx";
import {
  calculateServiceIncrement,
  cleanCheckFormInput,
  getCheckCreatedMessage,
  getCheckDetailUrl,
  hasCheckSteps,
  hasSnippet,
  isEmptyCheckScript,
  omitDisabledFields,
  tabHasRequiredFields,
} from "@/pages/checks/utils.ts";

export const CreateCheckForm = ({service, refetchQueries, checkCreatedMessage, title}: CreateCheckFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {user, userHasFeature} = useCurrentUserContext();
  const hasCloudStatusV2 = userHasFeature("cloudstatus_v2");

  const [showSnippetModal, setShowSnippetModal] = useState(false);
  const newlyCreatedCheckRef = useRef<{id: number; serviceType: MonitoringServiceType} | null>(null);

  const goBack = useCallback(() => {
    const previousPath = location.state?.from?.pathname;
    const defaultPath = service?.id ? `/checks/${service.id}` : "/checks";
    navigate(previousPath || defaultPath);
  }, [location.state?.from?.pathname, navigate, service?.id]);

  const {setCheckId, setVaultItems, getServiceVariableInputFromVaultItems, vaultItems} =
    useContext(NewCheckVaultItemsContext);
  useEffect(() => {
    setCheckId(service?.id);
  }, [service, setCheckId]);

  const [updateCheck, {data, reset}] = useMutation<UpdateCheckResponse>(GQL_UPDATE_CHECK, {
    refetchQueries: [GQL_CHECKS_QUERY2, ...(refetchQueries || [])],
    context: {noToast: true},
  });

  const updateCheckErrors =
    data?.response?.status === ResponseStatus.ERROR ? (data?.response?.message as ServerErrors) : undefined;
  const defaultValues = data?.response?.status === ResponseStatus.SUCCESS ? data?.response?.data : service;

  const transactionEditorRef = useRef();

  const [searchParams] = useSearchParams();
  const groupCheckServices = useMemo(
    () =>
      searchParams
        .get(QUERY.GROUP_CHECK_SERVICES)
        ?.split(",")
        .map((id) => Number(id)),
    [searchParams]
  );

  const {data: formData} = useSuspenseQuery<CheckFormResult>(GQL_CHECK_FORM, {fetchPolicy: "no-cache"});
  const [serviceType, setServiceType] = useState(() => {
    if (defaultValues?.monitoring_service_type) {
      return defaultValues.monitoring_service_type;
    }
    const serviceTypeParam = searchParams.get(QUERY.MONITORING_SERVICE_TYPE)?.toUpperCase();
    if (
      serviceTypeParam &&
      formData.monitoringServiceTypes.some(({types}) => types.some(({name}) => name === serviceTypeParam))
    ) {
      return serviceTypeParam as MonitoringServiceType;
    }
    return MonitoringServiceType.HTTP;
  });
  const serviceDef = formData!.serviceDefinitions.find((d) => d.service_type === serviceType)!;

  const getDefaultGroups = useCallback(
    () =>
      getDefaultMonitoringGroups({
        formData,
        serviceDef,
        preserveIds: defaultValues?.monitoring_groups.map((g) => g.id),
      }),
    [serviceDef, formData, defaultValues]
  );

  const methods = useForm<CreateCheckFormValues>({
    defaultValues: generateFormDefaults({
      service: defaultValues,
      svc: serviceDef,
      serviceType: serviceDef.service_type,
      mspInterval: formData.mspInterval,
      checkFrequency: formData.user.account.limit_check_frequency,
      contactGroups: formData.contactGroups.items,
      mspChoices: formData.mspChoices,
      defaultGroups: getDefaultGroups(),
      groupCheckServices,
    }),
    reValidateMode: "onChange",
    mode: "onChange",
    // @ts-expect-error incomplete form type
    resolver: yupResolver(
      Yup.object().shape({
        [ServiceField.THRESHOLD]: Yup.number()
          .optional()
          .nullable()
          .typeError(({originalValue}) =>
            originalValue ? `"${originalValue}" value must be an integer.` : "This field is required"
          )
          .when([QUERY.MONITORING_SERVICE_TYPE, ServiceField.INTERVAL], ([serviceType, interval], schema) => {
            return serviceType === MonitoringServiceType.TRANSACTION && interval < 5
              ? schema.max(
                  30,
                  "Micro Transaction Checks with an interval of less than 5 minutes should have a timeout of 30 seconds or less."
                )
              : schema;
          }),
        [ServiceField.RUM_APDEX_THRESHOLD]: Yup.number()
          .optional()
          .nullable()
          .typeError(({originalValue}) =>
            originalValue ? `"${originalValue}" value must be an integer.` : "This field is required"
          ),
        [ServiceField.TAGS]: Yup.array()
          .of(Yup.number())
          .test("max-tags", "A Check is limited to 20 Tags.", (value) => !value || value.length <= 20),
        [ServiceField.SCRIPT]: Yup.string().when([QUERY.MONITORING_SERVICE_TYPE], ([serviceType], schema) => {
          return hasCheckSteps(serviceType)
            ? schema.test(
                "has-steps",
                "Please add at least one step to this Check.",
                (value) => !isEmptyCheckScript(value)
              )
            : schema;
        }),
        [ServiceField.THIRD_PARTY_GROUP]: Yup.object().when(
          [QUERY.MONITORING_SERVICE_TYPE],
          ([serviceType], schema) => {
            return hasCloudStatusV2 && serviceType === MonitoringServiceType.CLOUDSTATUS
              ? schema.required("Please select a service to monitor.")
              : schema.nullable();
          }
        ),
        [ServiceField.THIRD_PARTY_COMPONENTS]: Yup.array().when(
          [QUERY.MONITORING_SERVICE_TYPE, ServiceField.THIRD_PARTY_MONITORING_TYPE],
          ([serviceType, monitoringType], schema) => {
            return hasCloudStatusV2 &&
              serviceType === MonitoringServiceType.CLOUDSTATUS &&
              monitoringType === ThirdPartyMonitoringType.SPECIFIC
              ? schema.min(1, "Please select at least one component to monitor.").of(
                  Yup.object().shape({
                    sub_component_monitoring_type: Yup.mixed<ThirdPartyMonitoringType>()
                      .oneOf(Object.values(ThirdPartyMonitoringType))
                      .nullable(),
                    sub_components: Yup.array().when(
                      "sub_component_monitoring_type",
                      ([subMonitoringType], subSchema) => {
                        return subMonitoringType === ThirdPartyMonitoringType.SPECIFIC
                          ? subSchema.min(1, "Please select at least one component to monitor.")
                          : subSchema;
                      }
                    ),
                  })
                )
              : schema;
          }
        ),
      })
    ),
  });

  const checkId = methods.watch(ServiceField.ID);
  const serviceCountLimitCheckRef = useRef<LimitCheckerComponent>();
  const serviceCountLimitCheckV1Ref = useRef<LimitCheckerComponent>();
  const subscriptionType = user!.account.subscription_type;
  useEffect(() => {
    // run limit check on check duplicate action
    if (!checkId && defaultValues) {
      const checkLimitRef =
        subscriptionType === AccountSubscriptionType.BILLING_2_0
          ? serviceCountLimitCheckRef
          : serviceCountLimitCheckV1Ref;
      checkLimitRef.current?.checkIfLimitHit();
    }
  }, [checkId, defaultValues, subscriptionType]);

  const vaultItemsJson = JSON.stringify(vaultItems);
  // use effect watch for changes to service variables and add them to the form data
  useEffect(() => {
    const formStayedOpenAfterSave = checkId === undefined && defaultValues?.id !== undefined;
    if (formStayedOpenAfterSave) {
      methods.setValue("service_variables", undefined);
      return;
    }
    if (defaultValues?.id === undefined) {
      const serviceVariables = getServiceVariableInputFromVaultItems();
      methods.setValue("service_variables", serviceVariables);
    }
  }, [vaultItemsJson, getServiceVariableInputFromVaultItems, defaultValues?.id, checkId, methods]);

  useEffect(() => {
    const newDefaults = generateFormDefaults({
      svc: serviceDef,
      mspInterval: formData.mspInterval,
      serviceType: serviceDef.service_type,
      checkFrequency: formData.user.account.limit_check_frequency,
      contactGroups: formData.contactGroups.items,
      mspChoices: formData.mspChoices,
      defaultGroups: getDefaultGroups(),
      service: defaultValues,
      groupCheckServices,
    });
    if (methods.getValues("monitoring_service_type") === serviceDef.service_type) {
      methods.reset(newDefaults);
      return;
    }
    const values = methods.getValues();
    const {dirtyFields} = methods.formState;
    const commonFields: (keyof CreateCheckFormValues)[] = [
      ServiceField.TAGS,
      ServiceField.CONTACT_GROUPS,
      ServiceField.ESCALATIONS,
      ServiceField.IS_UNDER_MANUAL_MAINTENANCE,
      ServiceField.PAUSE_UNDER_MANUAL_MAINTENANCE,
      ServiceField.MAINTENANCE_SCHEDULE_IDS,
    ];
    if (serviceDef.service_type !== MonitoringServiceType.CLOUDSTATUS) {
      commonFields.push(ServiceField.NAME);
    }
    const keptFields = commonFields.filter((field) => dirtyFields[field]);
    methods.reset(newDefaults, {keepFieldsRef: true, keepIsValid: true});
    keptFields.forEach((field) => {
      methods.setValue(field, values[field], {shouldDirty: true});
    });
    methods.trigger(keptFields);
  }, [methods, serviceDef, formData, getDefaultGroups, defaultValues, groupCheckServices]);

  const [tabsWithError, setTabsWithError] = useState<string[]>([]);
  const [serverErrors, setServerErrors] = useState<ServerErrors | undefined>(updateCheckErrors);

  useEffect(() => {
    const errorKeys = Object.keys(methods.formState.errors);
    const serverErrorIndex = errorKeys.indexOf("__all__");
    if (serverErrorIndex !== -1) {
      errorKeys.splice(serverErrorIndex, 1);
      const errorMessage = (methods.formState.errors as Record<string, {message?: string}>).__all__?.message;
      if (errorMessage && (!serverErrors || serverErrors.__all__?.[0] !== errorMessage)) {
        setServerErrors({__all__: [errorMessage]});
      }
    }
    if (errorKeys.length === 0 && tabsWithError.length === 0) {
      return;
    }
    if (errorKeys.length === 0) {
      setTabsWithError([]);
      return;
    }
    const newTabsWithErrorSet: Set<string> = new Set();
    errorKeys.forEach((key) => {
      const fieldTab = Object.values(baseSections).find(({fields}) => fields.includes(key as ServiceField));
      if (fieldTab) {
        newTabsWithErrorSet.add(fieldTab.id);
        return;
      }
      const fieldMap = advancedFields[key];
      if (fieldMap) {
        const advFieldTab = fieldMap[serviceDef.service_type] || fieldMap.default;
        newTabsWithErrorSet.add(advFieldTab);
        return;
      }
      console.warn(`No tab found for field: ${key}`);
    });
    const newTabsWithError = Array.from(newTabsWithErrorSet);
    if (
      newTabsWithError.length !== tabsWithError.length ||
      newTabsWithError.some((tab) => !tabsWithError.includes(tab))
    ) {
      setTabsWithError(newTabsWithError);
    }
  }, [methods.formState, serverErrors, serviceDef.service_type, tabsWithError]);

  const externalDomains = methods.watch(ServiceField.RUM_EXTERNAL_DOMAINS as keyof CreateCheckFormValues);
  const isAjaxDisabled = methods.watch(ServiceField.RUM_IS_AJAX_DISABLED as keyof CreateCheckFormValues);
  const externalDomainsState = methods.getFieldState(ServiceField.RUM_EXTERNAL_DOMAINS as keyof CreateCheckFormValues);
  const ajaxDisabledState = methods.getFieldState(ServiceField.RUM_IS_AJAX_DISABLED as keyof CreateCheckFormValues);

  const hasTraceRouteEnabled =
    methods.watch(ServiceField.GLOBAL_PING_NETWORK_CAPTURE_TRACEROUTE as keyof CreateCheckFormValues) === true;

  const handleSave: SubmitHandler<CreateCheckFormValues> = useCallback(
    async (saveData) => {
      const resp = await updateCheck({
        variables: {
          input: omitDisabledFields(cleanCheckFormInput(saveData), serviceDef.fields, userHasFeature),
        },
      });
      const response = resp.data?.response;
      const newService = saveData.id === undefined;
      const checkHasSnippet = hasSnippet(saveData.monitoring_service_type);
      if (response?.status === ResponseStatus.SUCCESS) {
        if (newService) {
          const message =
            checkCreatedMessage || getCheckCreatedMessage(saveData.monitoring_service_type, serviceDef?.force_interval);
          toast.success(message);
        }
        const checkID = response?.data.id;
        if (checkHasSnippet && newService) {
          saveData.id = checkID;
          setCheckId(checkID);
          setVaultItems([]);
          newlyCreatedCheckRef.current = {id: checkID!, serviceType: saveData.monitoring_service_type};
          setShowSnippetModal(true);
          return;
        }
        if (
          saveData.monitoring_service_type === MonitoringServiceType.RUM2 &&
          !newService &&
          (externalDomainsState.isDirty || ajaxDisabledState.isDirty)
        ) {
          toast.success("Your RUM snippet has been updated.");
          setShowSnippetModal(true);
          return;
        }

        reset();
        setVaultItems([]);
        if (newService) {
          const url =
            saveData.monitoring_service_type === MonitoringServiceType.PAGESPEED
              ? `/checks?search=${encodeURIComponent(checkID || "")}`
              : getCheckDetailUrl(checkID!, saveData.monitoring_service_type);
          navigate(url);
        } else {
          goBack();
        }
        return checkID;
      } else if (response?.status === ResponseStatus.ERROR) {
        const serverErrors = response?.message as ServerErrors;
        Object.entries(serverErrors).forEach(([key, value]) => {
          methods.setError(key as keyof CreateCheckFormValues, {
            message: Array.isArray(value) ? value.join("\n") : String(value),
          });
        });
      }
    },
    [
      ajaxDisabledState.isDirty,
      checkCreatedMessage,
      externalDomainsState.isDirty,
      goBack,
      methods,
      reset,
      serviceDef?.force_interval,
      serviceDef.fields,
      navigate,
      setCheckId,
      setVaultItems,
      updateCheck,
      userHasFeature,
    ]
  );

  const testRunnerForm = useMemo(() => {
    return serviceDef.has_feature_run_test ? (
      <CheckTestRunnerForm
        service={serviceDef}
        runTestMonitoringServers={formData.runTestMonitoringServers}
        monitoringGroupsArchMapper={formData.monitoringGroupsArchMapper}
        legacyURL={formData.legacyURL}
        transactionEditorRef={transactionEditorRef}
        withContextState
      />
    ) : null;
  }, [serviceDef, formData.runTestMonitoringServers, formData.monitoringGroupsArchMapper, formData.legacyURL]);

  const sections = useMemo<CreateCheckFormSections>(() => {
    const sections = {
      general: {
        ...baseSections.general,
        error: tabsWithError.includes(baseSections.general.id),
        html: () => (
          <CheckDetailsForm
            service={serviceDef}
            serviceType={serviceType}
            setServiceType={setServiceType}
            monitoringGroups={formData.monitoringGroups}
            monitoringGroupsArchMapper={formData.monitoringGroupsArchMapper}
            monitoringServiceTypes={formData.monitoringServiceTypes}
            mspInterval={formData.mspInterval}
            mspChoices={formData.mspChoices}
            contactGroups={formData.contactGroups}
            tags={formData.tags}
            snippets={formData.snippets}
            serviceUUID={defaultValues?.uuid}
            disableServiceTypeSelector={!!defaultValues}
            testRunnerForm={testRunnerForm}
          />
        ),
      }, // Always visible
      checkSteps: {
        ...baseSections.checkSteps,
        error: tabsWithError.includes(baseSections.checkSteps.id),
        html: () => (
          <CheckStepsForm
            service={serviceDef}
            serviceType={serviceType}
            steps={formData.stepsDef}
            mspChoices={formData.mspChoices}
            legacyURL={formData.legacyURL}
            transactionEditorRef={transactionEditorRef}
            testRunnerForm={testRunnerForm}
            handleSave={handleSave}
          />
        ),
      },
      validationnsecurity: {
        ...baseSections.validationnsecurity,
        error: tabsWithError.includes(baseSections.validationnsecurity.id),
        html: () => (
          <CheckSettingsForm
            serviceType={serviceType}
            service={serviceDef}
            mspChoices={formData.mspChoices}
            testRunnerForm={testRunnerForm}
          />
        ),
      },
      aggregationnalerts: {
        ...baseSections.aggregationnalerts,
        error: tabsWithError.includes(baseSections.aggregationnalerts.id),
        html: () => (
          <CheckAggregationForm
            serviceType={serviceType}
            service={serviceDef}
            mspChoices={formData.mspChoices}
            monitoringServiceTypes={formData.monitoringServiceTypes}
            tags={formData.tags}
          />
        ),
      },
      sensitivityAndTargeting: {
        ...baseSections.sensitivityAndTargeting,
        error: tabsWithError.includes(baseSections.sensitivityAndTargeting.id),
        html: () => (
          <CheckSensitivityAndTargetingForm
            serviceType={serviceType}
            service={serviceDef}
            mspChoices={formData.mspChoices}
            monitoringGroups={formData.monitoringGroups}
            tags={formData.tags}
            testRunnerForm={testRunnerForm}
          />
        ),
      }, // Always visible
      escalations: {
        ...baseSections.escalations,
        error: tabsWithError.includes(baseSections.escalations.id),
        html: () => (
          <div className="pulseMaxWidthXL">
            <div className="pulseTextH3Bold mb-4">Escalation Rules</div>
            <CheckEscalationForm contactGroups={formData.contactGroups.items} />
          </div>
        ),
      }, // Always visible
      maintenance_v2: {
        ...baseSections.maintenance_v2,
        isVisible: true,
        error: tabsWithError.includes(baseSections.maintenance_v2.id),
        html: () => <CheckMaintenanceFormV2 />,
      },
    };
    if (
      [MonitoringServiceType.TRANSACTION, MonitoringServiceType.API, MonitoringServiceType.PAGESPEED].includes(
        serviceType
      )
    ) {
      sections.checkSteps.isVisible = true;
    }
    if (
      [
        MonitoringServiceType.HTTP,
        MonitoringServiceType.FTP,
        MonitoringServiceType.SFTP,
        MonitoringServiceType.SMTP,
      ].includes(serviceType)
    ) {
      sections.validationnsecurity.isVisible = true;
    }
    if (serviceType === MonitoringServiceType.GROUP) {
      sections.aggregationnalerts.isVisible = true;
    }
    if (serviceType === MonitoringServiceType.CLOUDSTATUS) {
      sections.sensitivityAndTargeting.isVisible = false;
    }
    if (serviceType === MonitoringServiceType.RUM2) {
      sections.sensitivityAndTargeting.title = "Tracking & SLA";
    }
    if (serviceType === MonitoringServiceType.PAGESPEED) {
      sections.sensitivityAndTargeting.title = "Execution";
    }
    return sections;
  }, [serviceType, formData, serviceDef, defaultValues, handleSave, tabsWithError, testRunnerForm]);

  const activeTab = searchParams.get(QUERY.TAB);
  const defaultTab = activeTab ? Object.keys(sections).find((key) => key === activeTab.toLowerCase()) : null;
  const [tab, setTab] = useState(defaultTab || sections.general.id);

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const blocker = useBlocker(methods.formState.isDirty && !methods.formState.isSubmitSuccessful && !showUnsavedModal);

  useBeforeUnload((event) => {
    if (methods.formState.isDirty && !methods.formState.isSubmitSuccessful) {
      event.preventDefault();
      return "You have unsaved changes. Are you sure you want to discard your changes?";
    }
  });

  useEffect(() => {
    if (blocker.state !== "blocked") {
      if (showUnsavedModal) {
        setShowUnsavedModal(false);
      }
      return;
    }
    if (methods.formState.isDirty && !methods.formState.isSubmitSuccessful) {
      setShowUnsavedModal(true);
    } else {
      blocker.proceed();
    }
  }, [blocker, showUnsavedModal, methods.formState]);

  const isNewCheck = !defaultValues?.uuid;
  const interval = methods.watch(ServiceField.INTERVAL);
  const intervalState = methods.getFieldState(ServiceField.INTERVAL);
  const thresholdState = methods.getFieldState(ServiceField.THRESHOLD);

  useEffect(() => {
    if (intervalState.invalid || serviceType !== MonitoringServiceType.TRANSACTION) {
      return;
    }
    if (!thresholdState.isDirty && isNewCheck) {
      if (interval < 5) {
        methods.setValue(ServiceField.THRESHOLD, 30);
      } else {
        methods.setValue(ServiceField.THRESHOLD, defaultValues?.msp_threshold);
      }
    } else {
      methods.trigger(ServiceField.THRESHOLD);
    }
  }, [
    defaultValues?.msp_threshold,
    interval,
    intervalState.invalid,
    isNewCheck,
    methods,
    serviceType,
    thresholdState.isDirty,
  ]);

  return (
    <>
      <TabContentContainer
        sections={sections}
        title={title || "Add New Check"}
        tab={tab}
        setTab={setTab}
        hasRequiredFields={tabHasRequiredFields(serviceDef, tab)}
        actionsBar={
          <div className="d-flex flex-column flex-md-row gap-3 align-items-center flex-wrap justify-content-md-end justify-content-center">
            {methods.formState.isDirty && <UnsavedIndicator />}
            <div className="d-flex align-items-center justify-content-md-between justify-content-center gap-2">
              <Button variant="secondary" size="large" onClick={goBack}>
                Cancel
              </Button>
              <Button
                className="text-nowrap"
                size="large"
                type="submit"
                form="create-check-form"
                disabled={!methods.formState.isDirty}
              >
                {isNewCheck ? "Create Check" : "Save Changes"}
              </Button>
            </div>
          </div>
        }
      >
        <FormProvider {...methods}>
          <form
            id="create-check-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!checkId) {
                const checkLimitRef =
                  subscriptionType === AccountSubscriptionType.BILLING_2_0
                    ? serviceCountLimitCheckRef
                    : serviceCountLimitCheckV1Ref;
                checkLimitRef.current?.checkIfLimitHit().then((isLimitHit) => {
                  if (!isLimitHit || checkId) {
                    // @ts-expect-error incomplete form type
                    methods.handleSubmit(handleSave)(e);
                  }
                });
              } else {
                // @ts-expect-error incomplete form type
                methods.handleSubmit(handleSave)(e);
              }
            }}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                e.target instanceof HTMLElement &&
                (e.target.tagName === "INPUT" || e.target.tagName === "SELECT")
              ) {
                e.preventDefault();
              }
            }}
            role="presentation"
            noValidate
          >
            {serverErrors?.__all__ && <FormErrorAlerts errors={serverErrors?.__all__} className="mb-4" />}
            {!serverErrors?.__all__ && formData.user.account.plan.toUpperCase() !== Plan.Custom.toUpperCase() && (
              <InstantCheckout
                serviceType={serviceDef.service_type}
                userCanPurchase={formData.user.has_purchase_permission}
                accountCanPurchase={accountCanPurchase(
                  formData.user.account.type,
                  formData.accountBillingInfo.hasSubscription
                )}
                isNewCheck={isNewCheck}
              />
            )}

            <div className="pulse-color-gray-900">{sections[tab].html()}</div>
            {testRunnerForm && <div className="d-none">{testRunnerForm}</div>}
            <LimitCheckServiceLimitV2
              serviceType={serviceDef.service_type}
              serviceCountIncrement={calculateServiceIncrement(serviceType, hasTraceRouteEnabled)}
              ref={serviceCountLimitCheckRef}
            />
            <LimitCheckServiceLimitV1 serviceType={serviceDef.service_type} ref={serviceCountLimitCheckV1Ref} />
          </form>
        </FormProvider>
      </TabContentContainer>
      <CheckUnsavedModal
        show={showUnsavedModal}
        onCancel={() => {
          if (blocker.state === "blocked") {
            blocker.reset();
          }
        }}
        onConfirm={() => {
          if (blocker.state === "blocked") {
            blocker.proceed();
          } else {
            goBack();
          }
        }}
      />
      {defaultValues?.uuid && (
        <CheckSnippetModal
          show={showSnippetModal}
          onClose={() => {
            const target = newlyCreatedCheckRef.current;
            if (target) {
              newlyCreatedCheckRef.current = null;
              navigate(getCheckDetailUrl(target.id, target.serviceType));
            } else {
              goBack();
            }
          }}
          serviceType={serviceType}
          snippets={formData.snippets}
          uuid={defaultValues.uuid}
          externalDomains={(externalDomains as string | undefined) || ""}
          isAjaxDisabled={(isAjaxDisabled as boolean | undefined) || false}
        />
      )}
    </>
  );
};
